// Staff-raised / inbox-originated service request modal (Tickets CP1 + CP2).
// Contact picker = native select of workspace contacts (CompanyPicker is the
// searchable-picker precedent; searchable upgrade deferrable). Company auto-fills
// from the selected contact's company_id (NewJobModal's snapshot approach).
//
// Default (RequestsInbox "Log request"): source='internal', status 'new'.
// With `prefill` (ConversationDetail "Create request"): source='email',
// conversation_id stamped, contact/subject seeded from the thread.
//
// NOTE: the notify_new_portal_request AFTER-INSERT trigger fires on EVERY insert
// (not portal-only), so this bells workspace owners/admins too; not suppressed.

import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery, useMutation } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { X } from 'lucide-react'
import { toast } from 'sonner'

const field: React.CSSProperties = { padding: '8px 10px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box', width: '100%' }
const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 5 }

export interface LogRequestPrefill {
  contactId?: string
  subject?: string
  conversationId?: string
  source?: string   // 'internal' (default) | 'email'
}

export default function LogRequestModal({ open, onClose, onSaved, workspaceId, prefill }: {
  open: boolean
  onClose: () => void
  onSaved: () => void
  workspaceId: string | null
  prefill?: LogRequestPrefill
}) {
  const { profile } = useAuth()
  const [contactId, setContactId] = useState('')
  const [subject, setSubject] = useState('')
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState('normal')

  useEffect(() => {
    if (!open) return
    setContactId(prefill?.contactId ?? '')
    setSubject(prefill?.subject ?? '')
    setDescription('')
    setPriority('normal')
  }, [open, prefill])

  const { data: contacts = [] } = useQuery<Array<{ id: string; first_name: string; last_name: string | null; company_id: string | null; email: string | null }>>({
    queryKey: ['log-request-contacts', workspaceId],
    queryFn: async () => {
      if (!workspaceId) return []
      const { data, error } = await supabase.from('contacts').select('id, first_name, last_name, company_id, email').eq('workspace_id', workspaceId).order('first_name')
      if (error) { console.error('[requests] contacts query failed', error); throw error }
      return data ?? []
    },
    enabled: !!workspaceId && open,
  })

  const save = useMutation({
    mutationFn: async () => {
      if (!workspaceId) throw new Error('Not signed in')
      if (!contactId) throw new Error('Select a contact')
      if (!subject.trim()) throw new Error('Subject is required')
      const c = contacts.find(x => x.id === contactId)
      if (!c) throw new Error('Contact not found')
      const { error } = await supabase.from('portal_requests').insert({
        workspace_id: workspaceId,
        source: prefill?.source ?? 'internal',
        status: 'new',
        raised_by: profile?.id ?? null,
        conversation_id: prefill?.conversationId ?? null,
        contact_id: c.id,
        company_id: c.company_id,               // snapshot the contact's company
        contact_name: `${c.first_name} ${c.last_name ?? ''}`.trim(),
        contact_email: c.email,
        subject: subject.trim(),
        description: description.trim() || null,
        priority,
      })
      if (error) throw error
    },
    onSuccess: () => { toast.success('Request logged'); onSaved() },
    onError: (e: any) => toast.error(e?.message || 'Failed to log request'),
  })

  const canSave = !!contactId && !!subject.trim() && !save.isPending

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
          style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'var(--overlay-scrim)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <motion.div initial={{ scale: 0.97, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.97, opacity: 0 }} transition={{ duration: 0.15 }}
            onClick={e => e.stopPropagation()}
            style={{ width: '100%', maxWidth: 440, background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 16, padding: 20, display: 'flex', flexDirection: 'column', gap: 12, maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 style={{ fontSize: 16, fontWeight: 800, color: 'var(--ink-primary)', margin: 0, letterSpacing: '-0.02em' }}>{prefill ? 'Create request' : 'Log request'}</h2>
              <button type="button" onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', display: 'flex', padding: 2 }}><X style={{ width: 18, height: 18 }} /></button>
            </div>
            <div>
              <label style={lbl}>Contact</label>
              <select value={contactId} onChange={e => setContactId(e.target.value)} style={{ ...field, cursor: 'pointer' }}>
                <option value="">Select a contact…</option>
                {contacts.map(c => <option key={c.id} value={c.id}>{`${c.first_name} ${c.last_name ?? ''}`.trim()}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Subject</label>
              <input value={subject} onChange={e => setSubject(e.target.value)} autoFocus placeholder="No heat on second floor" style={{ ...field, fontWeight: 600 }} />
            </div>
            <div>
              <label style={lbl}>Description</label>
              <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} placeholder="Details…" style={{ ...field, resize: 'vertical' }} />
            </div>
            <div>
              <label style={lbl}>Priority</label>
              <select value={priority} onChange={e => setPriority(e.target.value)} style={{ ...field, cursor: 'pointer' }}>
                <option value="normal">Normal</option><option value="urgent">Urgent</option>
              </select>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
              <button type="button" onClick={onClose} style={{ padding: '8px 14px', background: 'var(--border-subtle)', border: 'none', borderRadius: 8, cursor: 'pointer', color: 'var(--ink-secondary)', fontSize: 13, fontWeight: 600 }}>Cancel</button>
              <button type="button" onClick={() => save.mutate()} disabled={!canSave}
                style={{ padding: '8px 16px', background: 'var(--accent)', border: 'none', borderRadius: 8, cursor: canSave ? 'pointer' : 'not-allowed', color: 'var(--accent-ink)', fontSize: 13, fontWeight: 700, opacity: canSave ? 1 : 0.5 }}>
                {save.isPending ? 'Saving…' : (prefill ? 'Create request' : 'Log request')}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
